import {useCallback, useState, useEffect} from 'react';

interface UseIndexDBReturn<T> {
  db: IDBDatabase | null;
  add: (key: string) => (data: T) => Promise<void>;
  get: (id: string) => Promise<{id: string; data: T}>;
  update: (id: string, data: T) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

export const useIndexDB = <T>(
  dbName: string,
  storeName: string,
  version: number = 1
): UseIndexDBReturn<T> => {
  const [db, setDb] = useState<IDBDatabase | null>(null);

  useEffect(() => {
    let mounted = true;

    // initialize database setup
    const dbOpen = () => {
      if (!window?.indexedDB) return;

      const req = window.indexedDB.open(dbName, version);

      req.onsuccess = (evt: Event) => {
        if (!mounted) return;

        const database = (evt.target as IDBOpenDBRequest).result;
        setDb(database);
      };

      req.onerror = (evt: Event) => {
        console.error('Database error: ', (evt.target as IDBOpenDBRequest).error);
      };

      req.onupgradeneeded = (evt: IDBVersionChangeEvent) => {
        const localDb = (evt.target as IDBOpenDBRequest).result;
        if (!localDb.objectStoreNames.contains(storeName)) {
          localDb.createObjectStore(storeName, {keyPath: 'id'});
        }
      };
    };

    // call database open
    dbOpen();

    return () => {
      mounted = false;
      if (db) {
        db.close();
      }
    };
  }, [dbName, storeName, version]);

  const add = useCallback(
    (key: string) =>
      (data: T): Promise<void> =>
        new Promise((resolve, reject) => {
          if (!db) throw new Error('Database not initialized.');

          const transaction = db.transaction(storeName, 'readwrite');
          const store = transaction.objectStore(storeName);
          const req = store.add({id: key, data});

          req.onsuccess = () => resolve();

          req.onerror = (evt) => {
            reject(new Error(`Error adding data: ${(evt.target as IDBRequest).error}`));
          };
        }),
    [db, storeName]
  );

  const get = useCallback(
    (id: string): Promise<{id: string; data: T}> =>
      new Promise((resolve, reject) => {
        if (!db) throw new Error('Database not initialized.');

        const transaction = db.transaction(storeName, 'readonly');
        const store = transaction.objectStore(storeName);
        const req = store.get(id);

        req.onsuccess = () => resolve(req.result);

        req.onerror = (evt: Event) => {
          reject(new Error(`Error getting data: ${(evt.target as IDBRequest).error}`));
        };
      }),
    [db, storeName]
  );

  const update = useCallback(
    (id: string, data: T): Promise<void> =>
      new Promise((resolve, reject) => {
        if (!db) throw new Error('Database not initialized.');

        const transaction = db.transaction(storeName, 'readwrite');
        const store = transaction.objectStore(storeName);

        const getIdReq = store.get(id);

        getIdReq.onsuccess = () => {
          const savedData = getIdReq.result;

          savedData.data = data;

          const updateImgReq = store.put(savedData);

          updateImgReq.onsuccess = () => resolve();

          updateImgReq.onerror = (evt: Event) => {
            reject(new Error(`Error updating key request: ${(evt.target as IDBRequest).error}`));
          };
        };

        getIdReq.onerror = (evt: Event) => {
          reject(new Error(`Error getting key request: ${(evt.target as IDBRequest).error}`));
        };
      }),
    [db, storeName]
  );

  const remove = useCallback(
    (id: string): Promise<void> =>
      new Promise((resolve, reject) => {
        if (!db) {
          reject(new Error('Database not initialized'));
          return;
        }

        const transaction = db.transaction(storeName, 'readwrite');
        const store = transaction.objectStore(storeName);
        const req = store.delete(id);

        req.onsuccess = () => {
          resolve();
        };

        req.onerror = (evt: Event) => {
          reject(new Error(`Error removing data: ${(evt.target as IDBRequest).error}`));
        };
      }),
    [db, storeName]
  );

  return {
    db,
    add,
    get,
    update,
    remove,
  };
};
